import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import type { SxProps } from "@mui/material/styles";
import { CAAC_URL, DAAC_URL } from "@polifonia/env";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { Typography } from "@/components/Typography";

/** The in-page anchor of the MAAC member table the MAAC card points to. */
export const MAAC_MEMBERS_ANCHOR_ID = "maac-members";

const styles = {
  grid: {
    display: "grid",
    gap: "20px",
    gridTemplateColumns: "repeat(auto-fill, minmax(min(280px, 100%), 1fr))",
  },
  card: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "24px",
    border: `1px solid ${theme.colors.staticFgLine}`,
    borderRadius: "12px",
    backgroundColor: theme.colors.white,
  },
  fullName: {
    color: theme.colors.staticFgNote,
  },
  body: {
    flex: 1,
  },
  action: {
    alignSelf: "flex-start",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

interface ProductCardProps {
  name: string;
  fullName: string;
  children: React.ReactNode;
  action: React.ReactNode;
}

const ProductCard: React.FC<ProductCardProps> = function ProductCard({
  name,
  fullName,
  children,
  action,
}) {
  return (
    <Box component="section" sx={styles.card}>
      <Box>
        <Typography variant="h3" fontWeight={700}>
          {name}
        </Typography>
        <Box sx={styles.fullName}>
          <Typography variant="note">{fullName}</Typography>
        </Box>
      </Box>
      <Box sx={styles.body}>{children}</Box>
      <Box sx={styles.action}>{action}</Box>
    </Box>
  );
};

interface ProductAccessCardsProps {
  /** `null` while the MAAC member list is still loading. */
  maacMemberCount: number | null;
}

/**
 * One card per Crescendo product. Only MAAC members and roles live in Admin
 * Center, so MAAC shows its member count and links to the table below; CAAC
 * and DAAC manage their own members, so their cards open the product instead
 * of showing a number Admin Center does not have.
 */
export const ProductAccessCards: React.FC<ProductAccessCardsProps> =
  function ProductAccessCards({ maacMemberCount }) {
    const { t } = useTranslation();

    return (
      <Box sx={styles.grid}>
        <ProductCard
          name="MAAC"
          fullName="Marketing Analytics and Communication Cloud"
          action={
            <Button
              variant="secondary"
              component="a"
              href={`#${MAAC_MEMBERS_ANCHOR_ID}`}
            >
              {t("userManagement.products.manageAccess", "Manage access")}
            </Button>
          }
        >
          {maacMemberCount === null ? (
            <Skeleton width="96px" height="40px" />
          ) : (
            <>
              <Typography variant="value">{maacMemberCount}</Typography>
              <Typography variant="body">
                {t("userManagement.products.memberCount", {
                  count: maacMemberCount,
                  defaultValue: "members",
                })}
              </Typography>
            </>
          )}
        </ProductCard>
        <ProductCard
          name="CAAC"
          fullName="Conversational Analytics and Communication Cloud"
          action={
            CAAC_URL ? (
              <Button
                variant="secondary"
                component="a"
                href={CAAC_URL}
                rel="noopener noreferrer"
              >
                {t("userManagement.caac.heroActionText")}
              </Button>
            ) : null
          }
        >
          <Typography variant="body">
            {t("userManagement.products.managedInProduct", {
              product: "CAAC",
              defaultValue: "Members and roles are managed in {{product}}.",
            })}
          </Typography>
        </ProductCard>
        <ProductCard
          name="DAAC"
          fullName="Data Analytics and Communication Cloud"
          action={
            DAAC_URL ? (
              <Button
                variant="secondary"
                component="a"
                href={DAAC_URL}
                rel="noopener noreferrer"
              >
                {t("userManagement.products.goToDaac", "Go to DAAC")}
              </Button>
            ) : null
          }
        >
          <Typography variant="body">
            {t("userManagement.products.managedInProduct", {
              product: "DAAC",
              defaultValue: "Members and roles are managed in {{product}}.",
            })}
          </Typography>
        </ProductCard>
      </Box>
    );
  };
